import showToast from '../../Components/Common/SweetAlert/ToastAlert'
import { createTask, fetchAssignedTaskDetails } from '../../redux/task/actions'
import { useAppDispatch, useAppSelector, type AppDispatch } from '../../redux/store'
import { selectUserTeamMemberInfo } from '../../redux/auth/selector'
import { useLoading } from '../../context/LoadingContext'
import type { User } from '../../redux/task/types'
import { sendTaskCreationNotification, formatTaskDueDateForEmail, formatTaskDueCountdown } from '../../utils/emailNotificationUtils'
import { EMAIL_CONFIG, isNotifyInviteesEnabled } from '../../config/emailConfig'

interface AddTaskParams {
  title: string
  dueDate: Date | null
  details: string
  link: string
  importance: string
  operationalManager: string | string[]
  functionalArea: string | string[]
  supervisor: string | string[]
  // New fields to preserve original user selections
  originalOperationalManagerSelection?: string[]
  originalFunctionalAreaSelection?: string[]
  originalSupervisorSelection?: string[]
  status: string
  createdBy: User
  required?: boolean
  teamMembers?: any[]
}

interface UseAddTaskResponse {
  addTask: (params: AddTaskParams) => Promise<void>
}

const useAddTask = (): UseAddTaskResponse => {
  const dispatch: AppDispatch = useAppDispatch()

  const { setLoading } = useLoading()

  const teamMemberDetails = useAppSelector(selectUserTeamMemberInfo)

  const teamMemberDetailsPayload = {
    teamMemberEmail: teamMemberDetails.email,
    teamMemberWorkdayId: teamMemberDetails.workdayId,
  }

  /**
   * Resolve the list of assignees (email + name) from the team members list using the same
   * AND-filter logic as the backend (TaskService.bulkAssignTeamMemberTask).
   *
   * @param teamMembers - full list of team members from the Redux store
   * @param operationalManager - resolved operational manager workday IDs (no ALL_ tokens)
   * @param functionalArea - resolved functional area strings (no ALL_ tokens)
   * @param supervisor - resolved supervisor workday IDs (no ALL_ tokens)
   */
  const resolveAssignees = (
    teamMembers: any[],
    operationalManager: string | string[],
    functionalArea: string | string[],
    supervisor: string | string[],
  ): Array<{ email: string; name: string }> => {
    const omIds = Array.isArray(operationalManager) ? operationalManager : [operationalManager]
    const faValues = Array.isArray(functionalArea) ? functionalArea : [functionalArea]
    const svIds = Array.isArray(supervisor) ? supervisor : [supervisor]

    const validOmIds = omIds.filter(v => v && v.trim() !== '')
    const validFaValues = faValues.filter(v => v && v.trim() !== '')
    const validSvIds = svIds.filter(v => v && v.trim() !== '')

    // Start with active team members only (matches backend: { status: "active" })
    let filtered = teamMembers.filter(member => member.status === 'active')

    if (validOmIds.length > 0) {
      filtered = filtered.filter(
        member => member.operationalManager && validOmIds.includes(member.operationalManager.workdayId)
      )
    }

    if (validFaValues.length > 0) {
      filtered = filtered.filter(
        member => member.functionalArea && validFaValues.includes(member.functionalArea)
      )
    }

    if (validSvIds.length > 0) {
      filtered = filtered.filter(
        member => member.supervisor && validSvIds.includes(member.supervisor.workdayId)
      )
    }

    // Extract unique assignees with email + name
    const seen = new Set<string>()
    const assignees: Array<{ email: string; name: string }> = []
    for (const member of filtered) {
      if (member.email && !seen.has(member.email)) {
        seen.add(member.email)
        const name =
          member.firstName && member.lastName
            ? `${member.firstName} ${member.lastName}`
            : member.firstName || member.email
        assignees.push({ email: member.email, name })
      }
    }
    return assignees
  }

  const addTask = async ({
    title,
    createdBy,
    details,
    dueDate,
    importance,
    link,
    operationalManager,
    functionalArea,
    supervisor,
    originalOperationalManagerSelection,
    originalFunctionalAreaSelection,
    originalSupervisorSelection,
    status,
    required,
    teamMembers = [],
  }: AddTaskParams): Promise<void> => {
    setLoading(true)
    try {
      // Convert operationalManager workdayId(s) to OperationalManager object(s)
      const operationalManagerArray: any[] = []
      if (operationalManager && teamMembers.length > 0) {
        const operationalManagerIds = Array.isArray(operationalManager) ? operationalManager : [operationalManager]
        operationalManagerIds.forEach(omId => {
          const teamMember = teamMembers.find((member: any) => 
            member.operationalManager && member.operationalManager.workdayId === omId
          )
          if (teamMember?.operationalManager) {
            operationalManagerArray.push(teamMember.operationalManager)
          }
        })
      }

      // Convert functionalArea to array if it's a string
      const functionalAreaArray = Array.isArray(functionalArea) ? functionalArea : [functionalArea]
      
      // Convert supervisor workdayId(s) to Supervisor object(s)
      const supervisorArray: any[] = []
      if (supervisor && teamMembers.length > 0) {
        const supervisorIds = Array.isArray(supervisor) ? supervisor : [supervisor]
        supervisorIds.forEach(sId => {
          const teamMember = teamMembers.find((member: any) => 
            member.supervisor && member.supervisor.workdayId === sId
          )
          if (teamMember?.supervisor) {
            supervisorArray.push(teamMember.supervisor)
          }
        })
      }

      const createdTask = await dispatch(createTask({ 
        title, 
        createdBy, 
        details, 
        dueDate, 
        importance, 
        link, 
        operationalManager: operationalManagerArray.length > 0 ? operationalManagerArray : undefined, 
        functionalArea: functionalAreaArray, 
        supervisor: supervisorArray, 
        // Include original selections to preserve user intent
        originalOperationalManagerSelection,
        originalFunctionalAreaSelection,
        originalSupervisorSelection,
        status,
        required
      })).unwrap()

      showToast('success', 'Task created successfully!')

      // ── Email notification ──────────────────────────────────────────────
      // Determine recipients based on environment:
      //   - dev / testing / staging (REACT_APP_NOTIFY_INVITEES=false): creator only
      //   - production              (REACT_APP_NOTIFY_INVITEES=true):  all assignees
      const notifyAll = isNotifyInviteesEnabled()

      console.log(`📧 [useAddTask] notifyAll=${notifyAll}, teamMembers.length=${teamMembers.length}, creatorEmail=${teamMemberDetails.email}`)

      const creatorFullName =
        teamMemberDetails.firstName && teamMemberDetails.lastName
          ? `${teamMemberDetails.firstName} ${teamMemberDetails.lastName}`
          : teamMemberDetails.email

      let recipients: Array<{ email: string; name: string }> = []

      if (notifyAll && teamMembers.length > 0) {
        // Production: send to all team members that match the assignment criteria
        recipients = resolveAssignees(teamMembers, operationalManager, functionalArea, supervisor)
        console.log(`📧 [useAddTask] resolveAssignees returned ${recipients.length} assignee(s):`, recipients.map(r => r.email))

        // Ensure creator is always included
        if (teamMemberDetails.email && !recipients.some(r => r.email === teamMemberDetails.email)) {
          recipients.push({ email: teamMemberDetails.email, name: creatorFullName })
          console.log(`📧 [useAddTask] Creator not in assignees — added creator: ${teamMemberDetails.email}`)
        }
      } else if (notifyAll && teamMembers.length === 0) {
        // notifyAll=true but teamMembers list is empty (not yet loaded) — fall back to creator
        console.warn(`📧 [useAddTask] notifyAll=true but teamMembers list is empty. Falling back to creator-only.`)
        if (teamMemberDetails.email) {
          recipients = [{ email: teamMemberDetails.email, name: creatorFullName }]
        }
      } else {
        // Dev / testing / staging: send only to the creator
        if (teamMemberDetails.email) {
          recipients = [{ email: teamMemberDetails.email, name: creatorFullName }]
        }
      }

      console.log(`📧 [useAddTask] Final recipients (${recipients.length}):`, recipients.map(r => r.email))

      if (recipients.length > 0) {
        const formattedDueDate = formatTaskDueDateForEmail(dueDate)
        const taskDueCountdown = formatTaskDueCountdown(dueDate)

        // Build devNote for dev/test/staging environments (same pattern as TaskEmailNotificationButton)
        // When notifyAll=false, compute the full intended list so the redirected email can show who
        // would have received it in production.
        let devNote: string | undefined
        if (!notifyAll) {
          const intendedAssignees = resolveAssignees(teamMembers, operationalManager, functionalArea, supervisor)
          const allIntended =
            teamMemberDetails.email && !intendedAssignees.some(r => r.email === teamMemberDetails.email)
              ? [...intendedAssignees, { email: teamMemberDetails.email, name: creatorFullName }]
              : intendedAssignees
          const intendedNote =
            allIntended.length > 0
              ? `Intended recipients (${allIntended.length}): ${allIntended.map(r => r.email).join(', ')}`
              : 'No assignees found for this task.'
          devNote = `[DEV/TEST MODE] This email was redirected to you (${teamMemberDetails.email}) instead of the actual recipients. ${intendedNote}`
        }

        let emailFailureCount = 0

        const emailPromises = recipients.map(({ email: recipientEmail, name: recipientName }) =>
          sendTaskCreationNotification(
            {
              to: recipientEmail,
              subject: EMAIL_CONFIG.SUBJECTS.TASK_CREATED(title),
              taskTitle: title,
              taskDetails: details,
              taskDueDate: formattedDueDate,
              taskDueCountdown,
              taskImportance: importance,
              taskLink: link,
              taskAppUrl: `${window.location.origin}/tasks`,
              creatorName: creatorFullName,
              recipientName,
              taskId: createdTask?.id,
              devNote,
            },
            (_failureMessage: string) => {
              // Track per-recipient failures (sendTaskCreationNotification swallows errors,
              // so we count them here via the callback to surface a toast after all settle)
              emailFailureCount++
              console.warn(`📧 Task creation email failed for recipient: ${recipientEmail}`)
            }
          )
        )

        // Fire-and-forget: don't block UI on email sending.
        // Use Promise.allSettled so we can show a warning toast if any emails failed.
        // (sendTaskCreationNotification catches its own errors and invokes onEmailFailure
        //  rather than rejecting, so Promise.all would never catch failures — use the
        //  failure counter from the callback instead.)
        Promise.allSettled(emailPromises).then(() => {
          if (emailFailureCount > 0) {
            console.error(`📧 ${emailFailureCount} of ${recipients.length} task creation email(s) failed to send.`)
            showToast('warning', 'Task created, but email notifications could not be sent.')
          } else {
            console.log(`📧 All ${recipients.length} task creation email(s) sent successfully.`)
          }
        })
      }
      // ────────────────────────────────────────────────────────────────────
    } catch (err) {
      console.error('Failed to add task:', err)
      showToast('error', 'Error creating task')
    } finally {
      setLoading(false)
      await dispatch(fetchAssignedTaskDetails(teamMemberDetailsPayload)).unwrap()
    }
  }

  return { addTask: addTask }
}

export default useAddTask
